import { useState, useCallback, useEffect, useMemo } from "react";
import type { EmojiShortcodeItem } from "./EmojiShortcodePopover";

interface UseEmojiPopoverProps {
  editorRef: React.MutableRefObject<any>;
}

interface UseEmojiPopoverReturn {
  showEmojiShortcodes: boolean;
  setShowEmojiShortcodes: React.Dispatch<React.SetStateAction<boolean>>;
  emojiFilter: string;
  setEmojiFilter: React.Dispatch<React.SetStateAction<string>>;
  emojiShortcodeIndex: number;
  setEmojiShortcodeIndex: React.Dispatch<React.SetStateAction<number>>;
  showEmojiPicker: boolean;
  setShowEmojiPicker: React.Dispatch<React.SetStateAction<boolean>>;
  allEmojis: any[];
  filteredEmojis: EmojiShortcodeItem[];
  insertEmoji: (emoji: string) => void;
  insertEmojiShortcode: (emoji: EmojiShortcodeItem) => void;
}

export function useEmojiPopoverState({
  editorRef,
}: UseEmojiPopoverProps): UseEmojiPopoverReturn {
  const [showEmojiShortcodes, setShowEmojiShortcodes] = useState(false);
  const [emojiFilter, setEmojiFilter] = useState("");
  const [emojiShortcodeIndex, setEmojiShortcodeIndex] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [allEmojis, setAllEmojis] = useState<any[]>([]);

  // Lazy-load emoji data — only fetched once, defers ~1MB JSON import
  useEffect(() => {
    import("@emoji-mart/data").then((mod) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setAllEmojis(Object.values((mod.default as any).emojis));
    });
  }, []);

  const filteredEmojis: EmojiShortcodeItem[] = useMemo(() => {
    if (!emojiFilter || emojiFilter.length < 2 || allEmojis.length === 0)
      return [];
    const lower = emojiFilter.toLowerCase();
    return (
      allEmojis
        .filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (e: any) =>
            e.id.includes(lower) ||
            e.keywords?.some((kw: string) => kw.includes(lower)),
        )
        .slice(0, 8)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((e: any) => ({
          id: e.id,
          name: e.name,
          native: e.skins[0].native,
        }))
    );
  }, [emojiFilter, allEmojis]);

  const insertEmoji = useCallback(
    (emoji: string) => {
      editorRef.current?.insertText(emoji);
      setShowEmojiPicker(false);
      editorRef.current?.focus();
    },
    [editorRef],
  );

  const insertEmojiShortcode = useCallback(
    (emoji: EmojiShortcodeItem) => {
      const tiptapEditor = editorRef.current?.getEditor();
      if (!tiptapEditor) return;
      const { from } = tiptapEditor.state.selection;
      const deleteFrom = from - emojiFilter.length - 1; // -1 for the :
      tiptapEditor.view.dispatch(
        tiptapEditor.state.tr.insertText(emoji.native, deleteFrom, from),
      );
      tiptapEditor.commands.focus();
      setShowEmojiShortcodes(false);
    },
    [editorRef, emojiFilter],
  );

  return {
    showEmojiShortcodes,
    setShowEmojiShortcodes,
    emojiFilter,
    setEmojiFilter,
    emojiShortcodeIndex,
    setEmojiShortcodeIndex,
    showEmojiPicker,
    setShowEmojiPicker,
    allEmojis,
    filteredEmojis,
    insertEmoji,
    insertEmojiShortcode,
  };
}
